import { memo } from 'react'
import isNil from 'lodash/isNil'
import { ViewWrapper, PageWrapper, ViewSpacerWrapper } from 'components/widgets'
import { ALL_ORGS_SELECTED, GatewaysColumnKeys, BEACONS_PATH, GATEWAYS_PATH } from 'lib/constants'
import { DetailsDrawer } from 'models'
import * as utils from './utils'
import { PreConfiguredTableView } from 'components/partials/TableViewV2'
import { SortOrder, TableToolEvent, RouteParams, trans, TranslationGroup, Gateway } from 'lib/types'
import { invertBinaryEnum } from 'lib/utils/common'

import { useHistory, useParams } from 'react-router-dom'
import { useQuery } from 'models/modelUtils'
import { RenderCounter } from 'components/widgets/RenderCounter'
import { getRequestV5 } from 'models/api/rest'
import { useGlobal } from 'contexts/GlobalProvider'
import { useGateways } from 'contexts/GatewaysProvider'
import { GatewayDetailDrawer } from 'components/partials/DrawerDetails/GatewayDetailDrawer'

function GatewaysPage() {
  const history = useHistory()
  const { id } = useParams<RouteParams>()
  const query = useQuery()

  const { loading, setLoading, gateways, setGateways, tableState, setTableState, pagination, setPagination } =
    useGateways()
  const { selectedOrgId } = useGlobal()

  function handleDrawerClose() {
    history.push({
      pathname: `${GATEWAYS_PATH}`,
      search: query.toString(),
    })
    DetailsDrawer.close()
  }

  function handleRowClick(row: Gateway) {
    if (row.id != id) {
      history.push({
        pathname: `${GATEWAYS_PATH}/${row.id}`,
        search: query.toString(),
      })
    }
    DetailsDrawer.show({
      drawerComponent: GatewayDetailDrawer,
      drawerProps: {
        gateway: row,
        close: handleDrawerClose,
      },
    })
  }

  function handleHeaderClick(header: string) {
    // I will need to ignore certain header clicks as sorting is missing for some columns
    const excludeList = ['eui']
    if (excludeList.includes(header)) return

    setLoading(true)
    getRequestV5({
      endpoint: '/gateways',
      headers: selectedOrgId && selectedOrgId != ALL_ORGS_SELECTED ? { org_id: selectedOrgId } : {},
      params: {
        filterBy:
          tableState.filterBy != utils.getFilterOptions()[0] // not all beacons
            ? { field: 'health', value: tableState.filterBy.toUpperCase() }
            : undefined,
        searchBy: { value: tableState.searchBy },
        sortBy: {
          field: header,
          order: tableState.sortBy.order === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending,
        },
      },
    }).then(response => {
      const data = response.data
      setGateways(data.data)
      setPagination(data.meta)
      setLoading(false)
      // setTableTools({ ...tableTools, pagination: data.meta })
    })
    setTableState(state => {
      return Object.assign({}, state, {
        sortBy: {
          field: header,
          order: state.sortBy.field === header ? invertBinaryEnum(state.sortBy.order) : SortOrder.Ascending,
        },
      })
    })
  }

  function handleToolEvent(toolEvent: TableToolEvent<string, GatewaysColumnKeys>) {
    if (toolEvent.filterByRequest) {
      setLoading(true)
      getRequestV5({
        endpoint: '/gateways',
        headers: selectedOrgId && selectedOrgId != ALL_ORGS_SELECTED ? { org_id: selectedOrgId } : {},
        params: {
          filterBy:
            toolEvent.filterByRequest != utils.getFilterOptions()[0] // not all beacons
              ? { field: 'health', value: toolEvent.filterByRequest.toUpperCase() }
              : undefined,
          searchBy: { value: tableState.searchBy },
        },
      }).then(response => {
        const data = response.data
        setGateways(data.data)
        setPagination(data.meta)
        setLoading(false)
      })
    } else if (toolEvent.searchByRequest || toolEvent.searchByRequest == '') {
      setLoading(true)
      getRequestV5({
        endpoint: '/gateways',
        headers: selectedOrgId && selectedOrgId != ALL_ORGS_SELECTED ? { org_id: selectedOrgId } : {},
        params: {
          filterBy:
            tableState.filterBy != utils.getFilterOptions()[0] // not all beacons
              ? { field: 'health', value: tableState.filterBy.toUpperCase() }
              : undefined,
          searchBy: { value: toolEvent.searchByRequest },
        },
      }).then(response => {
        const data = response.data
        setGateways(data.data)
        setPagination(data.meta)
        setLoading(false)
        // setTableTools({ ...tableTools, pagination: data.meta })
      })
    } else if (toolEvent.sortByRequest) {
      console.log('sort request', toolEvent.sortByRequest)

      setLoading(true)
      getRequestV5({
        endpoint: '/gateways',
        headers: selectedOrgId && selectedOrgId != ALL_ORGS_SELECTED ? { org_id: selectedOrgId } : {},
        params: {
          filterBy:
            tableState.filterBy != utils.getFilterOptions()[0] // not all beacons
              ? { field: 'health', value: tableState.filterBy.toUpperCase() }
              : undefined,
          searchBy: { value: tableState.searchBy },
          sortBy: { field: toolEvent.sortByRequest.field, order: toolEvent.sortByRequest.order },
        },
      }).then(response => {
        const data = response.data
        setGateways(data.data)
        setPagination(data.meta)
        setLoading(false)
        // setTableTools({ ...tableTools, pagination: data.meta })
      })
    }

    // set table state after tool event
    setTableState(state => ({
      filterBy: toolEvent.filterByRequest || state.filterBy,
      sortBy: toolEvent.sortByRequest || state.sortBy,
      searchBy: isNil(toolEvent.searchByRequest) ? state.searchBy : toolEvent.searchByRequest,
    }))
  }

  function handlePageChange(page: any, newPage: any) {
    console.log('page:', page, 'np:', newPage)
    setLoading(true)
    getRequestV5({
      endpoint: '/gateways',
      headers: selectedOrgId && selectedOrgId != ALL_ORGS_SELECTED ? { org_id: selectedOrgId } : {},
      params: {
        pagination: { currentPage: newPage },
        filterBy:
          tableState.filterBy != utils.getFilterOptions()[0] // all beacons selected in filter?
            ? { field: 'health', value: tableState.filterBy.toUpperCase() }
            : undefined,
        searchBy: { value: tableState.searchBy },
        sortBy: {
          field: tableState.sortBy.field,
          order: tableState.sortBy.order,
        },
      },
    }).then(response => {
      const data = response.data
      setGateways(data.data)
      setPagination(data.meta)
      setLoading(false)
    })
  }

  return (
    <PageWrapper>
      <ViewWrapper>
        <ViewSpacerWrapper>
          <RenderCounter name={'GatewaysPage'} />
          <PreConfiguredTableView
            sortedData={gateways}
            tableState={tableState}
            columnConfigs={utils.getColumnConfigs()}
            filterOptions={utils.getFilterOptions()}
            sortOptions={utils.getSortOptions()}
            getRowToolTip={utils.getGatewayTooltip}
            onHeaderClick={handleHeaderClick}
            onRowClick={handleRowClick}
            onToolEvent={handleToolEvent}
            selectedRowId={id}
            loading={loading}
            pagination={pagination}
            onPageChange={handlePageChange}
          >
            {/* <ActionButton onClick={() => utils.handleDownloadCSV(selectedOrgId || '')}>
              {common.download_csv}
            </ActionButton> */}
          </PreConfiguredTableView>
        </ViewSpacerWrapper>
      </ViewWrapper>
    </PageWrapper>
  )
}
export default memo(GatewaysPage)
